import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PopoverPanel, TriggerButton } from './popover';
import { getDayFlag, subscribeDayFlags } from './dayFlags';

// ============================================================================
// DatePicker — the form-controls standard for dates (form-controls.md
// "Date: A"): a button that reads "Tue, Sep 22, 2026" and opens a month
// grid with Today / Tomorrow / Next Sat shortcuts. Same on every device.
// Replaces the flatpickr version (which grew Modal-autofocus and Escape
// special cases) and every <input type="date">.
//
// Value contract is unchanged: `value` is a 'YYYY-MM-DD' string (or ''),
// `onChange(str)` gets the same shape ('' when cleared). All date math is
// LOCAL — 'YYYY-MM-DD' is never handed to new Date() (that parses as UTC
// midnight and moved "today's move" to yesterday at 8 PM).
//
// Props: value, onChange, placeholder, size ('md'|'sm'), disabled,
// clearable (default true — shows Clear when a date is set), min/max
// ('YYYY-MM-DD', days outside are disabled), ariaLabel, className.
// `className` used to carry inputCls from callers; it's applied to the
// trigger so those call sites keep their width/emphasis tweaks.
// ============================================================================

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export const parseYmd = (s) => {
    if (!s || typeof s !== 'string') return null;
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return Number.isNaN(d.getTime()) ? null : d;
};
export const toYmd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const formatYmdLong = (s) => {
    const d = parseYmd(s);
    return d ? `${d.toLocaleDateString('en-US', { weekday: 'short' })}, ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : '';
};

const startOfToday = () => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// Month grid on its own — DatePicker wraps it in a trigger + popover;
// TripStrip (moves) drops it straight into a tile's panel. `value` = the
// selected 'YYYY-MM-DD'; `rangeEnd` (optional) highlights value..rangeEnd
// as a window. Day flags (sql/094): 'full' days are hatched (pickable, a
// warning line once chosen); 'closed' days are struck and not pickable.
export function CalendarGrid({ value, onPick, min, max, ignoreFlags = false, rangeEnd, className = '' }) {
    const [, bumpFlags] = useState(0);
    useEffect(() => subscribeDayFlags(() => bumpFlags(n => n + 1)), []);
    const selected = parseYmd(value);
    const rangeEndD = parseYmd(rangeEnd);
    const today = startOfToday();
    const [view, setView] = useState(() => { const d = selected || today; return new Date(d.getFullYear(), d.getMonth(), 1); });
    useEffect(() => { const d = parseYmd(value); if (d) setView(new Date(d.getFullYear(), d.getMonth(), 1)); }, [value]);
    const minD = parseYmd(min), maxD = parseYmd(max);
    const inRange = (d) => (!minD || d >= minD) && (!maxD || d <= maxD);
    const gridStart = addDays(view, -view.getDay());
    const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    return (
        <div className={className}>
            <div className="flex items-center justify-between mb-1.5">
                <button type="button" aria-label="Previous month" onClick={() => setView(v => new Date(v.getFullYear(), v.getMonth() - 1, 1))} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 font-bold text-slate-700">‹</button>
                <div className="text-[13px] font-bold text-slate-800">{MONTHS[view.getMonth()]} {view.getFullYear()}</div>
                <button type="button" aria-label="Next month" onClick={() => setView(v => new Date(v.getFullYear(), v.getMonth() + 1, 1))} className="w-9 h-9 rounded-lg bg-slate-100 hover:bg-slate-200 font-bold text-slate-700">›</button>
            </div>
            <div className="grid grid-cols-7 gap-0.5">
                {DOW.map((d, i) => <div key={i} className="text-center font-mono text-[10px] text-slate-400 py-1">{d}</div>)}
                {days.map(d => {
                    const other = d.getMonth() !== view.getMonth();
                    const isToday = d.getTime() === today.getTime();
                    const on = selected && d.getTime() === selected.getTime();
                    const inWindow = selected && rangeEndD && d > selected && d <= rangeEndD;
                    const flag = ignoreFlags ? null : getDayFlag(toYmd(d));
                    const closed = flag?.kind === 'closed';
                    const full = flag?.kind === 'full';
                    const off = !inRange(d) || closed;
                    return (
                        <button
                            key={d.getTime()}
                            type="button"
                            disabled={off}
                            aria-label={d.toDateString() + (full ? ' — full' : closed ? ' — closed' : '')}
                            aria-pressed={!!on}
                            title={flag ? (flag.kind === 'full' ? `Full — no more jobs${flag.note ? ': ' + flag.note : ''}` : `Closed${flag.note ? ': ' + flag.note : ''}`) : undefined}
                            onClick={() => onPick?.(toYmd(d))}
                            className={`h-9 rounded-lg text-[13px] tabular-nums transition-colors ${
                                on ? 'bg-red-600 text-white font-bold' : inWindow ? 'bg-red-100 text-red-900 font-semibold' : other ? 'text-slate-400 hover:bg-slate-100' : 'text-slate-800 hover:bg-slate-100'
                            } ${isToday && !on ? 'shadow-[inset_0_0_0_1px_#cbd5e1]' : ''} ${full && !on ? 'text-red-800' : ''} ${closed ? 'line-through' : ''} disabled:opacity-30 disabled:cursor-not-allowed`}
                            style={full && !on && !inWindow ? { background: 'repeating-linear-gradient(135deg, transparent 0 3px, rgba(254,226,226,0.9) 3px 6px)' } : undefined}
                        >
                            {d.getDate()}
                        </button>
                    );
                })}
            </div>
            {!ignoreFlags && selected && getDayFlag(value)?.kind === 'full' && (
                <div className="mt-2 text-[11.5px] font-semibold text-red-800 bg-red-50 rounded-lg px-2 py-1.5">⚠ This day is marked Full — booking it anyway needs a lead's OK.</div>
            )}
        </div>
    );
}

export default function DatePicker({ value, onChange, placeholder = 'Pick a date…', size = 'md', disabled = false, clearable = true, min, max, ariaLabel, className = '', ignoreFlags = false }) {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const close = useCallback(() => setOpen(false), []);

    const selected = parseYmd(value);
    const today = startOfToday();
    const minD = parseYmd(min), maxD = parseYmd(max);
    const inRange = (d) => (!minD || d >= minD) && (!maxD || d <= maxD);

    const pick = (ymd) => { onChange?.(ymd); close(); };
    const toggle = () => { if (disabled) return; setOpen(o => !o); };

    const nextSat = addDays(today, (6 - today.getDay()) || 7);
    const quick = [
        { label: 'Today', d: today },
        { label: 'Tomorrow', d: addDays(today, 1) },
        { label: 'Next Sat', d: nextSat },
    ];

    return (
        <>
            <TriggerButton
                innerRef={anchorRef}
                onClick={toggle}
                open={open}
                size={size}
                disabled={disabled}
                placeholder={placeholder}
                empty={!selected}
                ariaLabel={ariaLabel}
                className={className}
                glyph={(
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>
                )}
            >
                {formatYmdLong(value)}
            </TriggerButton>
            <PopoverPanel open={open} onClose={close} anchorRef={anchorRef} width={304} ariaLabel="Choose a date" className="p-2.5">
                <CalendarGrid value={value} onPick={pick} min={min} max={max} ignoreFlags={ignoreFlags} />
                <div className="flex gap-1.5 mt-2">
                    {!ignoreFlags && (
                        <span className="self-center text-[10px] text-slate-400 mr-1 whitespace-nowrap" title="Hatched = Full (no more jobs) · struck = Closed">▨ full · <s>closed</s></span>
                    )}
                    {quick.map(q => (
                        <button key={q.label} type="button" disabled={!inRange(q.d)} onClick={() => pick(toYmd(q.d))} className="flex-1 h-8 px-1 rounded-lg border border-slate-200 bg-white text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-30 whitespace-nowrap">{q.label}</button>
                    ))}
                    {clearable && selected && (
                        <button type="button" onClick={() => { onChange?.(''); close(); }} className="h-8 px-2 rounded-lg text-[11px] font-semibold text-red-600 hover:bg-red-50">Clear</button>
                    )}
                </div>
            </PopoverPanel>
        </>
    );
}
